import { useEffect, useRef } from "react";
import { ArrowRight, PlayCircle } from "lucide-react";
import Reveal from "@/components/Reveal";

const CAR_IMG = "/fuelkers-car.png";

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center pt-32 pb-16 overflow-hidden bg-[#050505]">
      <style>{`
        @keyframes hero-drift { to { transform: translateX(-120px) } }
        @keyframes hero-spin { to { transform: rotate(360deg) } }
        @keyframes hero-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
        @keyframes hero-line { 0% { transform: translateX(0); opacity: 0 } 30% { opacity: 1 } 100% { transform: translateX(56px); opacity: 0 } }
        .hero-streaks { background: repeating-linear-gradient(115deg, transparent 0 60px, rgba(255,255,255,.025) 60px 62px); animation: hero-drift 22s linear infinite; }
        .hero-rays {
          background: repeating-conic-gradient(from 0deg at 50% 60%, rgba(0,160,160,.10) 0deg 2deg, transparent 2deg 11deg);
          -webkit-mask: radial-gradient(closest-side at 50% 60%, #000 8%, transparent 72%);
                  mask: radial-gradient(closest-side at 50% 60%, #000 8%, transparent 72%);
          animation: hero-spin 60s linear infinite;
        }
        .hero-car-float { animation: hero-float 6s ease-in-out infinite; }
        .hero-underline { position: relative; }
        .hero-underline::after { content: ""; position: absolute; left: 0; right: 0; bottom: 4px; height: 8px; background: #FFD700; opacity: .9; z-index: -1; transform: skewX(-12deg); }
        @media (prefers-reduced-motion: reduce) { .hero-streaks, .hero-rays, .hero-car-float { animation: none } }
      `}</style>

      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(120% 90% at 70% 10%, rgba(0,160,160,.12), transparent 55%), radial-gradient(90% 80% at 20% 100%, rgba(255,215,0,.06), transparent 60%)",
      }} />
      <div className="hero-streaks absolute -inset-[20%] pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00A0A0]/60 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
        {/* Car stage */}
        <Reveal>
          <CarStage />
        </Reveal>

        {/* Copy centered */}
        <div className="relative z-10 max-w-4xl mx-auto text-center mt-2">
          <Reveal delay={100}>
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-[#00A0A0]/30 bg-[#00A0A0]/5 backdrop-blur-xl text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#00A0A0] mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00A0A0] animate-pulse" style={{ boxShadow: "0 0 8px #00A0A0" }} />
              <span>FUELKERS · SISTEMA DE CRECIMIENTO PARA NEGOCIOS DEL MOTOR</span>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <h1 className="font-heading text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.96] tracking-tight uppercase">
              NO SOLO GENERAMOS ATENCIÓN.
              <br />
              <span className="hero-underline font-stencil text-[#00A0A0]">HACEMOS QUE TE ELIJAN.</span>
            </h1>
          </Reveal>

          <Reveal delay={320}>
            <p className="mt-7 text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed font-body">
              Construimos <span className="text-white">confianza que vende</span>: contenido, marca, posicionamiento y captación trabajando juntos para que tu negocio de automoción deje de competir por precio y se convierta en la referencia de su zona.
            </p>
          </Reveal>

          <Reveal delay={440}>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <a
                href="#contacto"
                data-testid="hero-cta-primary"
                className="group btn-angular inline-flex items-center gap-2 px-7 py-4 bg-[#FFD700] hover:bg-[#FFE34D] text-black font-stencil text-xs sm:text-sm uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5"
                style={{ boxShadow: "6px 6px 0 #00A0A0" }}
              >
                QUIERO QUE ME ELIJAN
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#comparativa"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-2 px-7 py-4 border border-white/25 hover:border-[#00A0A0] hover:bg-[#00A0A0]/10 text-white font-stencil text-xs sm:text-sm uppercase tracking-widest transition-all duration-300"
              >
                <PlayCircle className="w-4 h-4" />
                VER CÓMO FUNCIONA
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 text-[10px] uppercase tracking-widest">
        <span>Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-[#00A0A0] to-transparent" />
      </div>
    </section>
  );
}

/* ---------------- Car stage: real banner image + rays + glow + particles ---------------- */

function CarStage() {
  const stageRef = useRef(null);
  const smokeRef = useRef(null);
  const linesRef = useRef(null);
  const carBoxRef = useRef(null);

  // Smoke + speed lines driven by scroll velocity
  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let energy = 0;
    let acc = 0;
    let raf;
    let last = window.scrollY;
    let lastT = performance.now();

    const onScroll = () => {
      const y = window.scrollY;
      energy = Math.min(1, energy + Math.abs(y - last) / 240);
      last = y;
    };

    const wheels = [
      { x: 0.21, y: 0.8 },
      { x: 0.69, y: 0.8 },
    ];

    const puff = () => {
      const layer = smokeRef.current;
      if (!layer) return;
      const w = wheels[Math.random() < 0.5 ? 0 : 1];
      const size = 16 + Math.random() * 28;
      const p = document.createElement("div");
      p.style.cssText = `position:absolute;left:${w.x * 100}%;top:${w.y * 100}%;width:${size}px;height:${size}px;border-radius:50%;pointer-events:none;background:radial-gradient(circle, rgba(190,214,208,.32), rgba(190,214,208,0) 70%);filter:blur(4px);will-change:transform,opacity`;
      layer.appendChild(p);
      const dx = 60 + Math.random() * 130;
      const dy = -(8 + Math.random() * 44);
      const anim = p.animate(
        [
          { transform: "translate(-50%,-50%) scale(.5)", opacity: 0 },
          { opacity: 0.55, offset: 0.2 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(2.4)`, opacity: 0 },
        ],
        { duration: 950 + Math.random() * 450, easing: "ease-out" }
      );
      anim.onfinish = () => p.remove();
    };

    const loop = (t) => {
      const dt = Math.min(64, t - lastT);
      lastT = t;
      energy *= Math.pow(0.94, dt / 16.7);
      if (energy < 0.02) energy = 0;
      if (linesRef.current) linesRef.current.style.opacity = energy.toFixed(2);
      if (carBoxRef.current) carBoxRef.current.style.transform = `translateX(${(-14 * energy).toFixed(1)}px)`;
      acc += energy * 0.4 * (dt / 16.7);
      while (acc >= 1) {
        acc -= 1;
        puff();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const stage = stageRef.current;
    if (!stage) return;

    const heart = '<svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 20l-1.2-1.1C6 14.6 3 11.9 3 8.6 3 6 5 4 7.5 4c1.5 0 2.9.7 3.5 1.8C11.6 4.7 13 4 14.5 4 17 4 19 6 19 8.6c0 3.3-3 6-7.8 10.3L12 20z"/></svg>';
    const chat = '<svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M4 4h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V5a1 1 0 0 1 0-1z"/></svg>';
    const person = '<svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0z"/></svg>';
    const insta = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.2" cy="6.8" r="1.2" fill="currentColor" stroke="none"/></svg>';
    const euro = '<span style="font-weight:900;font-size:18px;line-height:1">€</span>';

    const kinds = [
      { h: heart, c: "#00E0C8" },
      { h: chat, c: "#ffffff" },
      { h: euro, c: "#FFD700" },
      { h: person, c: "#00E0C8" },
      { h: insta, c: "#ffffff" },
      { h: heart, c: "#FFD700" },
    ];
    // exhaust (rear, weighted) + front wheel
    const emitters = [
      { x: 0.93, y: 0.68 },
      { x: 0.93, y: 0.68 },
      { x: 0.2, y: 0.75 },
    ];

    const spawn = () => {
      const rect = stage.getBoundingClientRect();
      if (!rect.width) return;
      const em = emitters[Math.floor(Math.random() * emitters.length)];
      const k = kinds[Math.floor(Math.random() * kinds.length)];

      const p = document.createElement("div");
      p.style.cssText = `position:absolute;left:${em.x * 100}%;top:${em.y * 100}%;transform:translate(-50%,-50%);pointer-events:none;color:${k.c};filter:drop-shadow(0 0 6px currentColor);will-change:transform,opacity;z-index:6;display:flex;align-items:center;justify-content:center`;
      p.innerHTML = k.h;
      stage.appendChild(p);

      const driftX = Math.random() * 140 - 40;
      const riseY = -(150 + Math.random() * 120);
      const dur = 2600 + Math.random() * 1400;

      const anim = p.animate(
        [
          { transform: "translate(-50%,-50%) scale(.4)", opacity: 0 },
          { opacity: 1, offset: 0.18 },
          { opacity: 1, offset: 0.7 },
          { transform: `translate(calc(-50% + ${driftX}px), calc(-50% + ${riseY}px)) scale(1)`, opacity: 0 },
        ],
        { duration: dur, easing: "cubic-bezier(.25,.6,.3,1)" }
      );
      anim.onfinish = () => p.remove();
    };

    const iv = setInterval(spawn, 480);
    return () => clearInterval(iv);
  }, []);

  return (
    <div ref={stageRef} data-testid="hero-car-stage" className="relative w-full max-w-4xl mx-auto h-[240px] sm:h-[300px] lg:h-[360px]">
      {/* Rays */}
      <div className="hero-rays absolute inset-0 pointer-events-none" />
      {/* Glow */}
      <div className="absolute left-1/2 top-[55%] w-[115%] h-[150%] -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{
        background: "radial-gradient(closest-side, rgba(16,52,45,.95), rgba(8,22,19,.5) 52%, transparent 74%)",
      }} />

      {/* Speed lines (scroll-reactive) */}
      <div ref={linesRef} data-testid="hero-speed-lines" className="absolute inset-0 pointer-events-none" style={{ opacity: 0 }}>
        {[
          { top: "28%", left: "80%", w: 110, c: "rgba(0,224,200,.8)", d: 0 },
          { top: "40%", left: "86%", w: 150, c: "rgba(255,255,255,.6)", d: 0.15 },
          { top: "52%", left: "90%", w: 90, c: "rgba(0,224,200,.7)", d: 0.3 },
          { top: "63%", left: "88%", w: 130, c: "rgba(255,215,0,.55)", d: 0.45 },
          { top: "74%", left: "84%", w: 120, c: "rgba(0,224,200,.75)", d: 0.55 },
          { top: "83%", left: "78%", w: 95, c: "rgba(255,255,255,.5)", d: 0.25 },
        ].map((l, i) => (
          <span
            key={i}
            className="absolute h-[2px] rounded-full"
            style={{
              top: l.top,
              left: l.left,
              width: `${l.w}px`,
              background: `linear-gradient(to right, ${l.c}, transparent)`,
              animation: `hero-line .6s linear ${l.d}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Smoke layer (scroll-reactive) */}
      <div ref={smokeRef} data-testid="hero-smoke-layer" className="absolute inset-0 pointer-events-none" />

      {/* Car (transparent cutout from user's banner) */}
      <div className="hero-car-float absolute inset-0 flex items-center justify-center">
        <div ref={carBoxRef} className="relative w-[min(780px,94%)] will-change-transform">
          {/* Ground shadow */}
          <div className="absolute left-1/2 -bottom-2 w-[92%] h-5 -translate-x-1/2 rounded-full bg-black/80 blur-md" />
          <div className="absolute left-1/2 -bottom-1 w-[80%] h-3 -translate-x-1/2 rounded-full bg-[#00A0A0]/25 blur-lg" />
          <img
            src={CAR_IMG}
            alt="Coupé Fuelkers"
            className="relative w-full h-auto"
            style={{ filter: "drop-shadow(0 14px 28px rgba(0,0,0,.7)) drop-shadow(0 0 22px rgba(0,160,160,.28))" }}
            loading="eager"
          />
        </div>
      </div>

      {/* Floor fade */}
      <div className="absolute inset-x-0 -bottom-2 h-16 bg-gradient-to-b from-transparent to-[#050505] pointer-events-none" />
    </div>
  );
}
